import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppNames } from '@app/models/AppNames';
import { Pages } from '@app/models/Pages';
import { AppErrorDetails } from '@app/shared/components/error/error.model';
import { NADA_API_ERROR } from '@app/shared/constants/globalConstants';
import { environment } from 'src/environments/environment';

import { AppErrorStateService } from './app-error-state.service';
import { LoggingService } from './logging.service';
import { NotificationService } from './notification.service';
import { SpinnerStateService } from './spinner-state.service';

export const VENDOR_ERROR_PATTERNS: string[] = [
  // Portal
  'bootstrap',
  'header-scripts.ts',
  'contentWindow',

  // Salesforce
  'embeddedservice_bootstrap',
  'salesforceliveagent',
  'salesforce.com',
  'force.com',
  'salesforce-sites.com',

  // Pendo
  'pendo.io'
];

export const NON_FATAL_RUNTIME_ERROR_PATTERNS: string[] = [
  'AddProcessingDivElementWithMessageForMenu',
  'IsMobileView',
  'ExpressionChangedAfterItHasBeenCheckedError'
];

export const FATAL_RUNTIME_ERROR_PATTERNS: string[] = [
  // Empty for now
];

@Injectable({
  providedIn: 'root'
})
export class AppErrorService {
  private fatalErrorInProgress = false;

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly dialog: MatDialog,
    private readonly spinnerStateService: SpinnerStateService,
    private readonly notificationService: NotificationService,
    private readonly loggingService: LoggingService,
    private readonly appErrorStateService: AppErrorStateService
  ) { }

  private getAppName(): AppNames {
    const appName = sessionStorage.getItem('appName');
    return appName?.toLowerCase() === AppNames.UNITY_DIRECT.toLowerCase()
      ? AppNames.UNITY_DIRECT
      : AppNames.UNITY_EXPRESS;
  }

  handleRuntimeError(error: unknown): void {
    const normalizedError = this.unwrapError(error);
    const details = this.createRuntimeErrorDetails(normalizedError);

    if (
      this.matchesRuntimeErrorPattern(normalizedError, VENDOR_ERROR_PATTERNS)
    ) {
      this.logError('Vendor Runtime Error', details);
      this.consoleError('VENDOR ERROR', normalizedError);
      return;
    }

    if (
      this.matchesRuntimeErrorPattern(
        normalizedError,
        NON_FATAL_RUNTIME_ERROR_PATTERNS
      )
    ) {
      this.logError('Non-Fatal Runtime Error', details);
      this.consoleError('NON-FATAL APP ERROR', normalizedError);
      return;
    }

    if (
      this.matchesRuntimeErrorPattern(
        normalizedError,
        FATAL_RUNTIME_ERROR_PATTERNS
      )
    ) {
      this.logError('Known Fatal Runtime Error', details);
      this.consoleError('FATAL APP ERROR', normalizedError);
      this.handleFatalError(details);
      return;
    }

    this.logError('Unhandled Runtime Error', details);
    this.consoleError('UNHANDLED APP ERROR', normalizedError);

    if (this.isEnvProdOrBeta()) {
      return; // Don't nav to error
    }

    this.handleFatalError(details); // Nav to error
  }

  handleHttpError(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    suppressNotification = false,
    suppressNadaError = false
  ): void {
    const details = this.createHttpErrorDetails(error, request);

    if (error.status === 400 || error.status === 404) {
      this.removeSpinners();
      this.closeAllDialogs();
    }

    if (error?.error?.Message?.includes(NADA_API_ERROR)) {
      this.logError('NADA API Error', details);
      this.consoleError('NADA API ERROR', error);
      return;
    }

    this.logError('HTTP Error', details);
    this.consoleError(`HTTP ERROR ${error.status}`, error);

    if (error.status >= 500) {
      // Only 'nada-error-whitelist' suppresses a 500's fatal redirect, distinct
      // from (and narrower than) 'error-whitelist' which only hides toasts.
      if (suppressNadaError) {
        return;
      }
      this.handleFatalError(details);
      return;
    }

    this.handleNonFatalHttpError(error, suppressNotification);
  }

  resetFatalState(): void {
    this.fatalErrorInProgress = false;
  }

  private handleNonFatalHttpError(
    error: HttpErrorResponse,
    suppressNotification: boolean
  ): void {
    if (suppressNotification) return;

    const messages = this.getHttpErrorMessages(error);

    if (messages.length > 1) {
      this.notificationService.showMultipleError(messages);
      return;
    }

    this.notificationService.showError(messages[0] ?? 'An error occurred.');
  }

  private handleFatalError(details: AppErrorDetails): void {
    if (this.fatalErrorInProgress) {
      this.logError('Secondary Fatal Error', details);
      return;
    }

    this.fatalErrorInProgress = true;

    this.appErrorStateService.setError(details);

    this.removeSpinners();
    this.closeAllDialogs();

    this.zone.run(() => {
      this.router.navigate([Pages.Error]).catch(error => {
        this.consoleError('ERROR PAGE NAVIGATION FAILED', error);
      });
    });
  }

  private removeSpinners(): void {
    try {
      this.spinnerStateService.removeSpinner();
    } catch (error) {
      this.consoleError('SPINNER CLEANUP ERROR', error);
    }
  }

  private closeAllDialogs(): void {
    try {
      if (this.dialog?.openDialogs?.length > 0) {
        this.dialog?.closeAll();
      }
    } catch (error) {
      this.consoleError('DIALOG CLEANUP ERROR', error);
    }
  }

  private matchesRuntimeErrorPattern(
    error: unknown,
    patterns: string[]
  ): boolean {
    const errorText = this.getSearchableErrorText(error).toLowerCase();

    return patterns.some(pattern => errorText.includes(pattern.toLowerCase()));
  }

  private isEnvProdOrBeta(): boolean {
    return (
      environment.production ||
      environment.type === 'beta' ||
      environment.type === 'qa'
    );
  }

  private createRuntimeErrorDetails(error: any): AppErrorDetails {
    return {
      app: this.getAppName(),
      source: 'runtime',
      message: this.getErrorMessage(error) || 'Unknown application error',
      stack: this.getErrorStack(error),
      timestamp: new Date().toISOString(),
      route: this.getSafeRoute(),
      environment: environment.type,
      userAgent: navigator.userAgent
    };
  }

  private createHttpErrorDetails(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>
  ): AppErrorDetails {
    const messages = this.getHttpErrorMessages(error);

    return {
      app: this.getAppName(),
      source: 'http',
      message: messages[0] ?? error.message ?? 'HTTP request failed',
      stack: error?.error?.stack,
      timestamp: new Date().toISOString(),
      route: this.getSafeRoute(),
      environment: environment.type,
      userAgent: navigator.userAgent,
      status: error.status,
      statusText: error.statusText,
      method: request.method,
      url: request.url
    };
  }

  private getHttpErrorMessages(error: HttpErrorResponse): string[] {
    const messages: string[] = [];

    const mainMessage =
      error?.error?.Message ??
      error?.error?.message ??
      (typeof error?.error === 'string' ? error.error : undefined) ??
      error.message;

    if (mainMessage) {
      messages.push(mainMessage);
    }

    if (Array.isArray(error?.error?.Inner)) {
      for (const inner of error.error.Inner) {
        const innerMessage =
          typeof inner === 'string'
            ? inner
            : (inner?.message ?? inner?.Message);

        if (innerMessage) {
          messages.push(innerMessage);
        }
      }
    }

    return messages;
  }

  private unwrapError(error: unknown): any {
    const value = error as any;

    return value?.rejection ?? value?.ngOriginalError ?? value;
  }

  private getSearchableErrorText(error: unknown): string {
    const value = this.unwrapError(error);

    return [
      value?.message,
      value?.stack,
      value?.filename,
      value?.sourceURL,
      value?.error?.message,
      value?.error?.stack
    ]
      .filter(Boolean)
      .join('\n');
  }

  private getErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    return error?.message ?? error?.error?.message ?? String(error ?? '');
  }

  private getErrorStack(error: any): string | undefined {
    return error?.stack ?? error?.error?.stack;
  }

  private getSafeRoute(): string {
    const route = this.router.url || globalThis.location.pathname;

    return route.split('?')[0].split('#')[0];
  }

  private logError(category: string, details: AppErrorDetails): void {
    try {
      this.loggingService.sendErrorLevelMessage(category, this, {
        error: details
      });
    } catch (error) {
      this.consoleError('NGXLOGGER ERROR', error);
    }
  }

  private consoleError(title: string, error: unknown): void {
    console.error(
      `%c ${title} `,
      'font-weight: bold; font-size: 13px; background: #b00020; color: white; padding: 3px 6px; border-radius: 2px;',
      error
    );
  }
}
